import { GymTypes } from 'fitbeat.models';
import { Exercise, ExerciseFormat } from '../../../entities/exercise';
import { WorkoutSettings } from '../../../entities/user';
import { ExerciseSelection } from '../../../entities/workout';
import { PseudoRandomGenerator } from '../pseudorandomgenerator';

// TODO make filters
export class SpecialFormatExerciseSelector {
    public static filterFirstExerciseForFormatAndBodyGroup(
        exercises: Exercise[],
        selection: ExerciseSelection,
        workoutSettings: WorkoutSettings): Exercise | undefined {
        let exercise: Exercise | undefined;
        exercise = exercises.find((ex) => SpecialFormatExerciseSelector.doesFirstFormatAnd3BodyGroupsMatch(
            ex, [selection.format], workoutSettings.threeBodyGroups));
        if (!exercise) { // if no body group specific warmupdown, look for all body
            exercise = exercises.find(
                (ex) => SpecialFormatExerciseSelector.doesFirstFormatAnd3BodyGroupsMatch(
                ex, [selection.format], []));
        }
        return exercise;
    }

    public static filterMotiTips(
        allExercises: Exercise[],
        gymType: GymTypes,
        date: Date,
        exerciseFormat: ExerciseFormat,
        instructor: string,
        ) {
        let filtered = allExercises.filter((e) => {
            if (e.exerciseFormats == null || e.exerciseFormats.length === 0) {
                return false;
            }
            if (e.disable === true) {
                return false;
            }
            if (!e.voiceTipReviewStatuses || e.voiceTipReviewStatuses.length === 0) {
                return false;
            }

            const voiceTipReviewStatusForInstructor =
                e.voiceTipReviewStatuses.find((x) => x.ptName === instructor);
            if (voiceTipReviewStatusForInstructor === undefined) { return false; }
            if (voiceTipReviewStatusForInstructor && !voiceTipReviewStatusForInstructor.passed) {
                return false;
            }

            if (!SpecialFormatExerciseSelector.isCompatibleWithGymType(e, gymType)) {
                return false;
            }
            return e.exerciseFormats.findIndex((ef) => ef === exerciseFormat) !== -1;
        });

        filtered =
            filtered.filter((x) => gymType !== GymTypes.Home || x.videoModel === instructor);

        filtered = SpecialFormatExerciseSelector.shuffleWithDateSeed(date, filtered);

        return filtered;
    }

    public static getRandomSetMatchingFormatAndBodyGroups(
        allExercises: Exercise[],
        gymType: GymTypes,
        date: Date,
        filterFunctionForFormat: (exerciseFormat: ExerciseFormat) => boolean,
        randomise: boolean,
        instructor?: string) {
        let filtered = allExercises.filter((e) => {
            if (e.exerciseFormats == null || e.exerciseFormats.length === 0) {
                return false;
            }

            if (e.disable === true) {
                return false;
            }

            if (!SpecialFormatExerciseSelector.isCompatibleWithGymType(e, gymType)) {
                return false;
            }

            return e.exerciseFormats.findIndex((ef) => filterFunctionForFormat(ef)) !== -1;
        });

        filtered =
            filtered.filter((x) => gymType !== GymTypes.Home || x.videoModel === instructor);

        filtered.sort((a, b) => a.uniqueReferenceNumber != null
            ? a.uniqueReferenceNumber.toString().localeCompare(b.uniqueReferenceNumber.toString()) : 1 );

        if (!randomise) {
            const exercisesSelectedForDate
            = SpecialFormatExerciseSelector.exercisesSelectedForDate(filtered, date);
            return exercisesSelectedForDate;
        } else {
            filtered = SpecialFormatExerciseSelector.shuffleWithDateSeed(date, filtered);
            const firstRandomMatchingSet
                = SpecialFormatExerciseSelector.getFirstSetMatchingFormatAndBodyGroups(filtered);
            return firstRandomMatchingSet;
        }
    }

    private static isCompatibleWithGymType(e: Exercise, gymType: GymTypes) {
        if (e.compatibleGymTypes == null) {
            return true;
        }
        return e.compatibleGymTypes.indexOf(gymType) !== -1;
    }

    private static doesFirstFormatAnd3BodyGroupsMatch(
        exercise: Exercise, exerciseFormats: ExerciseFormat[], bigBodyGroups: string[]): boolean {
        if (exercise.exerciseFormats == null || exercise.exerciseFormats.length < 1 || exercise.bodyGroups == null) {
            return false;
        }
        if (exerciseFormats == null || exerciseFormats.length < 1 || bigBodyGroups == null) {
            return false;
        }
        const exerciseFormatToMatch = exerciseFormats[0];
        return exercise.exerciseFormats.indexOf(exerciseFormatToMatch) !== -1
            && Exercise.compare3BodyGroup(
                Exercise.get3BodyGroups(exercise.bodyGroups), bigBodyGroups);
    }

    private static getFirstSetMatchingFormatAndBodyGroups(
        exercises: Exercise[]): Exercise[] {
        const firstSetForFormatsAndBodyGroups = new Array<Exercise>();

        for (const exercise of exercises) {
            const exFormats = exercise.exerciseFormats;
            const exBigBodyGroups = Exercise.get3BodyGroups(exercise.bodyGroups);
            if (firstSetForFormatsAndBodyGroups.findIndex(
                (ex) => SpecialFormatExerciseSelector.doesFirstFormatAnd3BodyGroupsMatch(
                    ex, exFormats, exBigBodyGroups)) === -1) {
                firstSetForFormatsAndBodyGroups.push(exercise);
            }
        }
        return firstSetForFormatsAndBodyGroups;
    }

    private static exercisesSelectedForDate(filtered: Exercise[], date: Date) {
        const exerciseArrays = SpecialFormatExerciseSelector.splitIntoArrayPerFormatAndBody(filtered);
        const exercisesSelectedForDate = exerciseArrays.map(
            (exs) => SpecialFormatExerciseSelector.getExerciseForDayOfYearSequence(exs, date));
        return exercisesSelectedForDate;
    }

    private static splitIntoArrayPerFormatAndBody(filtered: Exercise[]): Exercise[][] {
        const exercisesPerFormat = {};
        filtered.forEach((ex) => {
            const exFormats = ex.exerciseFormats.toString();
            const exBigBodyGroups = Exercise.get3BodyGroups(ex.bodyGroups).toString();
            const exKeyPerFormatAndBody = exFormats + exBigBodyGroups;
            let exercisesForThisFormat: Exercise[] | undefined = exercisesPerFormat[exKeyPerFormatAndBody];
            if (exercisesForThisFormat == null) {
                exercisesForThisFormat = [];
                exercisesPerFormat[exKeyPerFormatAndBody] = exercisesForThisFormat;
            }
            exercisesForThisFormat.push(ex);
        });
        const exercisesPerFormatArrays: Exercise[][] = [];
        for (const key of Object.keys(exercisesPerFormat)) {
            const exs = exercisesPerFormat[key];
            if (exs != null) {
                exercisesPerFormatArrays.push(exs);
            }
        }

        return exercisesPerFormatArrays;
    }
    private static getExerciseForDayOfYearSequence(exs: Exercise[], date: Date): Exercise {
        const dayOfYear = this.dayOfYear(date);
        const dayOfYearIndex = dayOfYear % exs.length;
        return exs[dayOfYearIndex];
    }

    private static dayOfYear(current: Date) {
        const currentNum = current.getTime();
        const year =  new Date(current).getUTCFullYear();
        const yearstart = Date.UTC(year, 0, 1);
        const daylengthinms = 1000 * 60 * 60 * 24;
        return Math.floor((currentNum - yearstart) / daylengthinms);
    }

    private static shuffleWithDateSeed(date: Date, exercises: Exercise[]): Exercise[] {
        const dateSeed = PseudoRandomGenerator.getSeedFromDate(date);
        const shuffledFilteredExercises = PseudoRandomGenerator.shuffleArrayPseudoRandomly(
            exercises,
            dateSeed,
        );
        return shuffledFilteredExercises;
    }
}
